<template>
  <Container
    id="heading"
  >
    <HeroCard
      :heading="t('jc.view.rules.hero.title')"
      :paragraph="t('jc.view.rules.hero.text')"
      :image="{
        src: 'images/view/rules/hero_banner.png',
        alt: t('jc.view.rules.hero.banner.alt'),
      }"
      :float="{
        src: 'images/skin_renders/compositions/upgrade_gear.png',
        alt: t('jc.view.rules.hero.float.alt'),
      }"
      :label="t('jc.view.rules.hero.button.label')"
      :icon="'link-simple'"
      :to="localePath({ name: 'legal-terms' })"
    />
  </Container>

  <Container
    id="common-rules"
  >
    <ContentCard
      :heading="t('jc.view.rules.common.title')"
      :paragraph="t('jc.view.rules.common.text')"
    >
      <IconSection
        :sections="commonRules"
        count
      />
    </ContentCard>
  </Container>

  <Container
    id="call-to-action"
  >
    <ImageCard
      :heading="t('jc.view.rules.callToAction.title')"
      :paragraph="t('jc.view.rules.callToAction.text')"
      :image="{
        src: 'images/view/rules/call_to_action_background.png',
        alt: t('jc.view.rules.callToAction.background.alt'),
      }"
    >
      <NuxtLink
        :target="'_blank'"
        :to="config.public.discordUrl"
        :external="true"
        :title="t('jc.view.rules.callToAction.button.title')"
        class="bg-primary hover:bg-primary-highlighted active:bg-primary-selected text-white
          text-center rounded py-2 px-4 mt-10 mx-auto font-semibold"
      >
        <font-awesome-icon
          :icon="['fab', 'discord']"
          class="mr-2"
        />
        <span>{{ t('jc.view.rules.callToAction.button.label') }}</span>
      </NuxtLink>
    </ImageCard>
  </Container>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

const { t } = useI18n()
const localePath = useLocalePath()
const config = useRuntimeConfig()

definePageMeta({
  nameKey: 'rules',
  head: {
    imageUrl: '/images/view/rules/meta_preview.png',
  },
})

// items in 'common-rules' (list) section
const commonRules = [
  {
    icon: 'face-smile',
    title: t('jc.view.rules.common.section.nice.title'),
    text: t('jc.view.rules.common.section.nice.text'),
  },
  {
    icon: 'shield',
    title: t('jc.view.rules.common.section.privacy.title'),
    text: t('jc.view.rules.common.section.privacy.text'),
  },
  {
    icon: 'user-astronaut',
    title: t('jc.view.rules.common.section.avatar.title'),
    text: t('jc.view.rules.common.section.avatar.text'),
  },
  {
    icon: 'people-group',
    title: t('jc.view.rules.common.section.team-mention.title'),
    text: t('jc.view.rules.common.section.team-mention.text'),
  },
  {
    icon: 'envelopes-bulk',
    negateIcon: true,
    title: t('jc.view.rules.common.section.no-spam.title'),
    text: t('jc.view.rules.common.section.no-spam.text'),
  },
  {
    icon: 'face-pleading',
    negateIcon: true,
    title: t('jc.view.rules.common.section.no-begging.title'),
    text: t('jc.view.rules.common.section.no-begging.text'),
  },
  {
    icon: 'face-swear',
    negateIcon: true,
    title: t('jc.view.rules.common.section.no-mobbing.title'),
    text: t('jc.view.rules.common.section.no-mobbing.text'),
  },
  {
    icon: 'hand-point-right',
    negateIcon: true,
    title: t('jc.view.rules.common.section.no-discrimination.title'),
    text: t('jc.view.rules.common.section.no-discrimination.text'),
  },
  {
    icon: 'landmark',
    negateIcon: true,
    title: t('jc.view.rules.common.section.no-politics.title'),
    text: t('jc.view.rules.common.section.no-politics.text'),
  },
  {
    icon: 'siren-on',
    negateIcon: true,
    title: t('jc.view.rules.common.section.no-piracy.title'),
    text: t('jc.view.rules.common.section.no-piracy.text'),
  },
  {
    icon: 'hand-fist',
    negateIcon: true,
    title: t('jc.view.rules.common.section.no-pornography.title'),
    text: t('jc.view.rules.common.section.no-pornography.text'),
  },
  {
    icon: 'megaphone',
    negateIcon: true,
    title: t('jc.view.rules.common.section.no-ads.title'),
    text: t('jc.view.rules.common.section.no-ads.text'),
  },
  {
    icon: 'billboard',
    title: t('jc.view.rules.common.section.own-ads-selected.title'),
    text: t('jc.view.rules.common.section.own-ads-selected.text'),
  },
  {
    icon: 'bug',
    title: t('jc.view.rules.common.section.no-bugusing.title'),
    text: t('jc.view.rules.common.section.no-bugusing.text'),
  },
  {
    icon: 'brain',
    title: t('jc.view.rules.common.section.use-brain.title'),
    text: t('jc.view.rules.common.section.use-brain.text'),
  },
]
</script>

<style scoped lang="postcss">
:deep(.preview-img img) {
  @apply w-full h-full object-center object-scale-down max-h-32
}
</style>
